import React, { useEffect, useState } from 'react';
import { secureAdminFetch } from '../utils/security';
import { API_BASED_URL } from '../config.js';

const AdminEditGuidelines = () => {
  const [guidelines, setGuidelines] = useState(['']);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const fetchGuidelines = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetch(`${API_BASED_URL}api/guidelines/internal`);
        if (res.ok) {
          const data = await res.json();
          if (Array.isArray(data) && data.length > 0) {
            setGuidelines(data);
          } else {
            setGuidelines(['']);
          }
        } else {
          setError('Failed to load guidelines');
        }
      } catch (_) {
        setError('Failed to load guidelines');
      } finally {
        setLoading(false);
      }
    };
    fetchGuidelines();
  }, []);

  const updateGuideline = (index, value) => {
    setGuidelines(prev => prev.map((g, i) => (i === index ? value : g)));
  };

  const addGuideline = () => setGuidelines(prev => [...prev, '']);
  const removeGuideline = (index) => setGuidelines(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const cleaned = guidelines.map(g => (g || '').trim()).filter(g => g.length > 0);
      const res = await secureAdminFetch(`${API_BASED_URL}api/guidelines/internal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ guidelines: cleaned })
      });
      if (res.ok) {
        setSuccess('Guidelines saved successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to save guidelines');
      }
    } catch (e) {
      setError('Failed to save guidelines');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading guidelines...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white shadow-xl rounded-lg p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Edit Internal Guidelines</h1>
          <p className="text-sm text-gray-600 mb-4">These appear on the booking page for Internal users as “Mandatory Guidelines for Internal Users”.</p>
          {error && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{error}</div>}
          {success && <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">{success}</div>}

          <div className="space-y-3">
            {guidelines.map((g, idx) => (
              <div key={idx} className="flex items-start space-x-2">
                <input
                  type="text"
                  value={g}
                  onChange={(e) => updateGuideline(idx, e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder={`Guideline #${idx + 1}`}
                />
                <button
                  type="button"
                  onClick={() => removeGuideline(idx)}
                  className="w-8 h-8 bg-red-100 text-red-700 rounded hover:bg-red-200 flex items-center justify-center text-xs font-bold"
                  title="Remove guideline"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addGuideline}
              className="px-4 py-2 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
            >
              Add Guideline
            </button>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEditGuidelines;


